import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { Box } from "@mui/material";
import { useMyHrEmployee } from "my-hr/hooks";
import {
  MyTimeCalendar,
  MyTimeInfoDialog,
  MyTimeNewRequestDialog,
  MyTimeUserApprovalDialog,
} from "my-time/components";
import {
  employeeAdmissions,
  employeeAdmissionsIsMobile,
} from "my-time/configs/table-defs";
import {
  useMyTimeActualDate,
  useMyTimeAdmission,
  useMyTimeRecordingPeriod,
} from "my-time/hooks";
import { useMyTimeUserApproval } from "my-time/hooks/use-my-time-user-approval";
import { parseActions } from "my-time/utils";
import { countDailyHours } from "my-time/utils";
import { OcCard, OcDataTable, OcInfoDialog } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcButtonGroup, useOcTableState, useOcProperty } from "oc/hooks";
import { getFullname } from "oc/utils";

type MyTimeAdmissionCardProps = {
  contractCode: string;
  employeeCode: string;
  year: number;
  month: number;
  changeMyTimeEnabled: boolean;
  changeWorkingTimeEnabled: boolean;
  isNotExaminer: boolean;
  isMobile: boolean;
  userApprovalEnabled: boolean;
  onAllTimesNullChange?: (v: boolean) => void;
};

export const MyTimeAdmissionCard = ({
  contractCode,
  employeeCode,
  year,
  month,
  changeMyTimeEnabled,
  changeWorkingTimeEnabled: editWorkingTimeEnabled,
  isNotExaminer,
  isMobile,
  userApprovalEnabled,
  onAllTimesNullChange,
}: MyTimeAdmissionCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const recordingPeriod = useMyTimeRecordingPeriod();

  const [selectedRowId, setSelectedRowId] = useState<string | number | undefined>(undefined);
  const [matchingMyTimeitems, setMatchingMyTimeitems] = useState<any[]>([]);

  const { date, handleDateChange } = useMyTimeActualDate();

  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });
  const employeeName = getFullname(employeeData);

  const { data: myTimeEnabledData } = useOcProperty({
    resource: "myTime",
    key: "changeMyTimeEnabled",
  });

  const { isApproved } = useMyTimeUserApproval();

  const requestsEnabled = myTimeEnabledData === "true";

  const [openNewRequestDialog, setOpenNewRequestDialog] = useState({
    state: false,
    action: null as null | string,
    data: null as any,
  });

  const [openInfoDialog, setOpenInfoDialog] = useState({
    state: false,
    data: null as any,
  });

  const [openShortInfoDialog, setOpenShortInfoDialog] = useState({
    state: false,
    text: "",
  });

  const handleCloseNewRequestDialog = () => {
    setOpenNewRequestDialog({ state: false, action: null, data: null });
  };

  const { data, loading, error, events } = useMyTimeAdmission({
    contractCode,
    year,
    month,
    changeMyTimeEnabled,
    recordingPeriod,
    isApproved,
  });

  const countTimeData = (date: string | Date) => {
    const result = countDailyHours({ date, list: data });
    return result;
  };

  const parsedData = data?.map((item) => {
    const newItem = { ...item };
    newItem.countTimeData = countTimeData;
    return newItem;
  });

  const list = {
    rows: parsedData,
    total: data?.length,
  };

  const handleSelectRow = ({ action, row }: { action: string; row: any }) => {
    setOpenInfoDialog({ state: false, data: null });
    setOpenNewRequestDialog({ state: false, action: null, data: null });
    setOpenShortInfoDialog({ state: false, text: "" });
    parseActions({
      requestsEnabled,
      editWorkingTimeEnabled,
      action,
      row,
      list,
      role: "user",
      setOpenNewRequestDialog,
      setOpenInfoDialog,
      setOpenShortInfoDialog,
      history,
      employeeCode,
      contractCode,
      recordingPeriod,
      isNotExaminer,
      isApproved,
    });
    return;
  };

  const buttonsDef = ["OC_CALENDAR", "OC_TABLE"];
  const { OcButtonGroup, selectedButton, handleButtonChange } =
    useOcButtonGroup({
      id: `MyTimeAdmissionCard`,
      buttonsDef,
    });

  const [selectedRowData, setSelectedRowData] = useState(null);

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
      setSelectedRowData(o);

      const selectedDate = moment(o?.date).format("YYYY.MM.DD");

      const foundMyTimeItems = list?.rows?.filter(
        (item) => moment(item?.date).format("YYYY.MM.DD") === selectedDate,
      );

      if (foundMyTimeItems?.length > 1) {
        setMatchingMyTimeitems(foundMyTimeItems);
      } else {
        setMatchingMyTimeitems([]);
      }
    }
  };

  let tableId = "employeeAdmissionsTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "title",
    pageSize: 5,
  });

  if (employeeAdmissions && employeeAdmissions?.columns) {
    const defValues = Object.values(employeeAdmissions?.columns);
    let myTimeActions = null;
    const newDef = defValues.map((item) => {
      if (item?.id === "myTimeActions" || item?.id === "myTimeAdminActions") {
        myTimeActions = {
          ...item,
          changeMyTimeEnabled: changeMyTimeEnabled,
          changeWorkingTimeEnabled: editWorkingTimeEnabled,
        } as typeof item & {
          changeMyTimeEnabled: boolean;
          changeWorkingTimeEnabled: boolean;
        };
        return myTimeActions;
      }
      return item;
    });
    employeeAdmissions.columns = newDef;
  }

  const handleMoreClick = (date: string | Date) => {
    handleButtonChange(1);
    const foundItem = list?.rows?.find(
      (item) =>
        new Date(item?.date).toISOString() === new Date(date).toISOString(),
    );

    const selectedDate = new Date(foundItem?.start).toLocaleDateString();

    setTimeout(() => {
      const element = document.getElementById(selectedDate);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 200);
  };

  useEffect(() => {
    if (isMobile) {
      handleButtonChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const tableDef = isMobile ? employeeAdmissionsIsMobile : employeeAdmissions;

  const filteredTableDef = {
    id: employeeAdmissions?.id,
    columns: employeeAdmissions?.columns?.filter(
      (column) =>
        !employeeAdmissionsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const allTimesNull: boolean = (Array.isArray(list.rows) ? list.rows : [])
    .filter(
      (it) =>
        !(it?.__typename === "AdmissionResponse" && it?.myTimeType !== "WORKING_TIME")
    )
    .every((it) => it?.startTime === null && it?.endTime === null);

  useEffect(() => {
    onAllTimesNullChange?.(allTimesNull);
  }, [allTimesNull, onAllTimesNullChange]);

  return (
    <OcCard label={t("MY_WORK_TIME_REPORTS")} loading={loading} error={!!error}>
      {userApprovalEnabled && (
        <MyTimeUserApprovalDialog
          list={list?.rows || []}
          loading={loading}
          allTimesNull={allTimesNull}
        />
      )}
      <OcInfoDialog
        open={openShortInfoDialog?.state}
        text={t(openShortInfoDialog?.text)}
        handleDialogClose={() => setOpenShortInfoDialog({ state: false, text: "" })}
        onClose={() => setOpenShortInfoDialog({ state: false, text: "" })}
        isMobile={isMobile}
      />
      <MyTimeInfoDialog
        open={openInfoDialog?.state}
        handleClose={() => setOpenInfoDialog({ state: false, data: null })}
        data={openInfoDialog?.data}
        employeeName={employeeName ?? ""}
        isMobile={isMobile}
      />
      <MyTimeNewRequestDialog
        contractCode={contractCode}
        open={openNewRequestDialog?.state}
        action={openNewRequestDialog?.action ?? ""}
        data={openNewRequestDialog?.data}
        handleClose={handleCloseNewRequestDialog}
        list={data}
        isMobile={isMobile}
        selectedRowData={selectedRowData}
      />
      {!isMobile && (
        <Box
          id="button-group"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          padding="18px 0px"
        >
          <OcButtonGroup />
        </Box>
      )}
      <Box padding="16px 0px">
        {selectedButton === "OC_TABLE" && (
          <OcDataTable
            tableDef={tableDef}
            tableId={tableId}
            tableData={tableData}
            setTableData={setTableData}
            data={list}
            loading={loading}
            onSelectRow={isMobile ? onSelectRow : handleSelectRow} //For some reason it didn't want to work based on the established logic, that's why I decided on ternary.
            isNotExaminer={isNotExaminer}
            filteredTableDef={filteredTableDef}
            selectedRowId={selectedRowId}
            handleMoreButton={handleSelectRow}
            matchingMyTimeitems={matchingMyTimeitems}
          />
        )}
        {selectedButton === "OC_CALENDAR" && (
          <MyTimeCalendar
            date={date}
            onDateChange={handleDateChange}
            contract={contractCode}
            events={events}
            handleSelectRow={handleSelectRow}
            handleMoreClick={handleMoreClick}
            isNotExaminer={isNotExaminer}
            countTimeData={countTimeData}
          />
        )}
      </Box>
    </OcCard>
  );
};

export default MyTimeAdmissionCard;
